import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Card,
  Modal
} from "react-bootstrap";
import ModalImage from "react-modal-image";
import ModalVideo from "react-modal-video";
import "react-modal-video/css/modal-video.min.css";
import { v4 as uuidv4 } from "uuid";
import { FaEdit, FaTrash } from "react-icons/fa";
import styled from "styled-components";

import HeaderTwo from "../../components/HeaderTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import Pagination from "../../components/Pagination";
import FooterTwo from "../../components/FooterTwo";

// Styled Components
const Styles = styled.div`
  .thumbnail-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
  }

  .video-thumbnail,
  .gallery-media {
    width: 100%;
    height: 100%;
    object-fit: cover;
    background-size: cover;
    background-position: center;
    cursor: pointer;
  }

  .play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 36px;
    color: white;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    padding: 10px 14px;
  }

  .action-icons {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 8px;
  }

  .action-icons .icon {
    color: white;
    background: rgba(0, 0, 0, 0.5);
    padding: 6px;
    border-radius: 50%;
    cursor: pointer;
    transition: background 0.2s;
    font-size: 16px;
  }

  .action-icons .icon:hover {
    background: rgba(0, 0, 0, 0.8);
  }

  .gallery-card {
    border: none;
    border-radius: 10px;
    overflow: hidden;
  }

  .gallery-card .card-body {
    padding: 12px;
  }

  .gallery-card .card-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 4px;
  }

  .gallery-card .card-text {
    font-size: 14px;
    color: #666;
  }

  .preview-container img,
  .preview-container video {
    max-width: 100%;
    border-radius: 8px;
  }

  .error-message {
    text-align: center;
    padding: 20px;
    color: #ff0000;
  }
`;

const Gallery = () => {
  const [galleryItems, setGalleryItems] = useState([
    {
      id: uuidv4(),
      galleryImage: "imagegallery-01.jpg",
      isVideo: false,
      title: "Beautiful Landscape",
      description: "A scenic view"
    },
    {
      id: uuidv4(),
      isVideo: true,
      url: "https://www.youtube.com/watch?v=hLjxON8r3MU",
      title: "Sample Video 1",
      description: "YouTube video"
    },
    {
      id: uuidv4(),
      isVideo: true,
      url: "https://www.youtube.com/watch?v=sBxaHKiykvY",
      title: "Sample Video 2",
      description: "Another YouTube video"
    },
    {
      id: uuidv4(),
      galleryImage: "imagegallery-04.jpg",
      isVideo: false,
      title: "Ocean Waves",
      description: "Coastal scene"
    }
  ]);

  const [isOpen, setIsOpen] = useState(false);
  const [videoId, setVideoId] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    youtubeUrl: "",
    file: null,
    previewUrl: null,
    isVideo: false
  });
  const [editItem, setEditItem] = useState(null);

  // Modal state for upload/edit
  const [showUploadModal, setShowUploadModal] = useState(false);

  const handleOpenUpload = () => {
    resetForm();
    setEditItem(null);
    setShowUploadModal(true);
  };

  const handleCloseUpload = () => {
    setShowUploadModal(false);
  };

  // File handling
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setFormData({
      ...formData,
      file,
      previewUrl,
      isVideo: file.type.startsWith("video")
    });
  };

  const handleInputChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleUpload = () => {
    const newItem = {
      id: uuidv4(),
      galleryImage: formData.file ? formData.previewUrl : "",
      isVideo: formData.isVideo || !!formData.youtubeUrl,
      url: formData.youtubeUrl || undefined,
      title: formData.title || "Untitled",
      description: formData.description || "No description"
    };
    setGalleryItems([...galleryItems, newItem]);
    resetForm();
  };

  const handleUpdate = () => {
    setGalleryItems(
      galleryItems.map((item) =>
        item.id === editItem.id
          ? {
              ...item,
              galleryImage: formData.file
                ? formData.previewUrl
                : item.galleryImage,
              isVideo: formData.youtubeUrl ? true : formData.isVideo,
              url: formData.youtubeUrl || item.url,
              title: formData.title || item.title,
              description: formData.description || item.description
            }
          : item
      )
    );
    resetForm();
    setEditItem(null);
  };

  const handleDelete = (id) =>
    setGalleryItems(galleryItems.filter((item) => item.id !== id));

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      title: item.title,
      description: item.description,
      youtubeUrl: item.url || "",
      previewUrl: item.galleryImage,
      file: null,
      isVideo: item.isVideo
    });
    setShowUploadModal(true); // open modal for edit
  };

  const resetForm = () =>
    setFormData({
      title: "",
      description: "",
      youtubeUrl: "",
      file: null,
      previewUrl: null,
      isVideo: false
    });

  const extractVideoId = (url) => {
    try {
      const urlObj = new URL(url);
      let videoId = null;
      if (urlObj.hostname.includes("youtube.com") && urlObj.searchParams.get("v")) {
        videoId = urlObj.searchParams.get("v");
      } else if (urlObj.hostname.includes("youtu.be")) {
        videoId = urlObj.pathname.split("/")[1];
      }
      return videoId;
    } catch (error) {
      console.error("Invalid YouTube URL:", error);
      return null;
    }
  };

  const openVideoModal = (url) => {
    const videoId = extractVideoId(url);
    if (videoId) {
      setVideoId(videoId);
      setIsOpen(true);
    } else {
      alert("Invalid YouTube URL. Please check the video link.");
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    setVideoId("");
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = galleryItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(galleryItems.length / itemsPerPage);

  return (
    <Styles>
      <div className="main-wrapper gallery-page">
        <HeaderTwo />
        <BreadcrumbBox title="Gallery" />

        {/* Upload button */}
        <div className="text-center mb-4">
          <Button variant="primary" onClick={handleOpenUpload}>
            Upload Media
          </Button>
        </div>

        {/* Gallery Grid */}
        <section className="gallery-page-area">
          <Container>
            <Row>
              {currentItems.map((data) => (
                <Col lg="4" sm="6" key={data.id} className="mb-4">
                  <Card className="gallery-card shadow-sm">
                    <div className="thumbnail-wrapper">
                      {data.isVideo ? (
                        <>
                          {data.url ? (
                            <div
                              className="video-thumbnail"
                              style={{
                                backgroundImage: `url(https://img.youtube.com/vi/${extractVideoId(
                                  data.url
                                )}/hqdefault.jpg)`
                              }}
                              onClick={() => openVideoModal(data.url)}
                            >
                              <div className="play-icon">▶</div>
                            </div>
                          ) : (
                            <video
                              src={data.galleryImage}
                              className="gallery-media"
                              controls
                            />
                          )}
                        </>
                      ) : (
                        <ModalImage
                          small={data.galleryImage}
                          large={data.galleryImage}
                          alt={data.title}
                          className="gallery-media"
                        />
                      )}
                      <div className="action-icons">
                        <FaEdit
                          className="icon edit-icon"
                          onClick={() => handleEdit(data)}
                          title="Edit"
                        />
                        <FaTrash
                          className="icon delete-icon"
                          onClick={() => handleDelete(data.id)}
                          title="Delete"
                        />
                      </div>
                    </div>
                    <Card.Body>
                      <Card.Title>{data.title}</Card.Title>
                      <Card.Text>{data.description}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
              <Col md="12" className="text-center">
                <Pagination
                  activePage={currentPage}
                  totalPages={totalPages}
                  onChange={setCurrentPage}
                />
              </Col>
            </Row>
          </Container>
        </section>

        {/* Modal for Upload / Edit */}
        <Modal show={showUploadModal} onHide={handleCloseUpload} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editItem ? "Edit Media" : "Upload New Media"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter media title"
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter description"
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Upload File (Image/Video)</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>YouTube URL (Optional)</Form.Label>
                <Form.Control
                  type="text"
                  name="youtubeUrl"
                  value={formData.youtubeUrl}
                  onChange={handleInputChange}
                  placeholder="Enter YouTube URL"
                />
              </Form.Group>

              {formData.previewUrl && (
                <div className="preview-container mt-3">
                  {formData.isVideo ? (
                    <video src={formData.previewUrl} controls className="preview-media" />
                  ) : (
                    <img
                      src={formData.previewUrl}
                      alt="Preview"
                      className="preview-media"
                    />
                  )}
                </div>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseUpload}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                editItem ? handleUpdate() : handleUpload();
                handleCloseUpload();
              }}
            >
              {editItem ? "Update" : "Upload"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Video Modal */}
        {isOpen && videoId ? (
          <ModalVideo
            channel="youtube"
            autoplay
            isOpen={isOpen}
            videoId={videoId}
            onClose={closeModal}
            youtube={{
              autoplay: 1,
              mute: 0,
              rel: 0
            }}
          />
        ) : (
          isOpen && (
            <div className="error-message">
              <p>Video unavailable. Please try another video.</p>
              <Button variant="secondary" onClick={closeModal}>
                Close
              </Button>
            </div>
          )
        )}

        <FooterTwo />
      </div>
    </Styles>
  );
};

export default Gallery;
